<template>
  <Teleport v-if="isClientMounted" to="body">
    <div
      v-if="isClientMounted && isSheetRendered"
      class="vf-o-bottom-sheet-layer"
      :class="[
        layerClass,
        {
          'vf-o-bottom-sheet-layer--open': isSheetOpen || isSheetDragging,
        },
      ]"
    >
      <button
        type="button"
        class="vf-o-bottom-sheet-backdrop vf-backdrop-blur-surface"
        :aria-label="backdropAriaLabel"
        @click="handleBackdropClick"
      />

      <section
        :id="panelId"
        ref="sheetElement"
        class="vf-o-bottom-sheet"
        :class="[
          sheetClass,
          {
            'vf-o-bottom-sheet--open': isSheetOpen && !isSheetDragging,
            'vf-o-bottom-sheet--dragging': isSheetDragging,
          },
        ]"
        :style="resolvedSheetInlineStyle"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="ariaLabelledby"
        @pointerdown="handleSheetPointerDown"
        @touchstart="handleSheetTouchStart"
        @touchmove="handleSheetTouchMove"
        @touchend="handleSheetTouchEnd"
        @touchcancel="handleSheetTouchCancel"
        @transitionend="handleSheetTransitionEnd"
      >
        <button
          type="button"
          class="vf-o-bottom-sheet__handle"
          :aria-label="handleAriaLabel"
          @click="handleHandleClick"
          @pointerdown.stop="handleHandlePointerDown"
        >
          <span class="vf-o-bottom-sheet__handle-pill"></span>
        </button>

        <div
          ref="sheetContentElement"
          class="vf-o-bottom-sheet__content"
          :class="contentClass"
        >
          <slot />
        </div>
      </section>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import {
  lockOverlayScroll,
  unlockOverlayScroll,
} from "~/core/utils/overlayScrollLock.ts";

const SHEET_CLOSE_DRAG_THRESHOLD = 96;
const SHEET_TRANSITION_DURATION_MS = 320;
const SHEET_TOUCH_GUARD_DELTA_THRESHOLD = 6;

const props = defineProps({
  rendered: {
    type: Boolean,
    default: false,
  },
  open: {
    type: Boolean,
    default: false,
  },
  dragging: {
    type: Boolean,
    default: false,
  },
  sheetInlineStyle: {
    type: [Object, Array, String],
    default: undefined,
  },
  panelId: {
    type: String,
    default: undefined,
  },
  ariaLabelledby: {
    type: String,
    default: undefined,
  },
  backdropAriaLabel: {
    type: String,
    default: "Close panel",
  },
  handleAriaLabel: {
    type: String,
    default: "Collapse panel",
  },
  layerClass: {
    type: [String, Array, Object],
    default: "",
  },
  sheetClass: {
    type: [String, Array, Object],
    default: "",
  },
  contentClass: {
    type: [String, Array, Object],
    default: "",
  },
  modelValue: {
    default: undefined,
  },
  draggable: {
    type: Boolean,
    default: true,
  },
  closeOnEsc: {
    type: Boolean,
    default: true,
  },
  closeOnBackdrop: {
    type: Boolean,
    default: true,
  },
  closeOnHandle: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits([
  "backdrop-click",
  "handle-click",
  "sheet-pointerdown",
  "handle-pointerdown",
  "sheet-transitionend",
  "update:modelValue",
  "close",
]);

const isClientMounted = ref(false);
const sheetElement = ref<HTMLElement | null>(null);
const sheetContentElement = ref<HTMLElement | null>(null);

const internalRendered = ref(false);
const internalOpen = ref(false);
const internalDragging = ref(false);
const internalTranslateY = ref(0);
const internalDragStartY = ref(0);
const internalDragDidMove = ref(false);
const suppressHandleClick = ref(false);
const activePointerId = ref<number | null>(null);
const dragStartedFromHandle = ref(false);
const hasScrollLock = ref(false);
const touchStartY = ref<number | null>(null);

let openFrameId: number | null = null;
let closeTimeoutId: number | null = null;

const isModernMode = computed(() => props.modelValue !== undefined);

const isSheetRendered = computed(() =>
  isModernMode.value ? internalRendered.value : props.rendered
);

const isSheetOpen = computed(() =>
  isModernMode.value ? internalOpen.value : props.open
);

const isSheetDragging = computed(() =>
  isModernMode.value ? internalDragging.value : props.dragging
);

const resolvedSheetInlineStyle = computed(() => {
  if (!isModernMode.value) {
    return props.sheetInlineStyle;
  }

  if (!internalDragging.value) {
    return undefined;
  }

  return {
    transform: `translateY(${internalTranslateY.value}px)`,
    transition: "none",
  };
});

const ensureScrollLock = () => {
  if (hasScrollLock.value) return;

  // A fixed-position body has to be restored on close and can flash for one
  // frame on mobile browsers. Overflow locking is sufficient for the sheet
  // (its touch guards already prevent pull-through) and needs no page reflow.
  lockOverlayScroll({ useFixedPosition: false });
  hasScrollLock.value = true;
};

const releaseScrollLock = () => {
  if (!hasScrollLock.value) return;

  unlockOverlayScroll();
  hasScrollLock.value = false;
};

const cancelPendingOpen = () => {
  if (typeof window === "undefined" || openFrameId === null) return;

  window.cancelAnimationFrame(openFrameId);
  openFrameId = null;
};

const clearCloseTimeout = () => {
  if (closeTimeoutId === null) return;

  clearTimeout(closeTimeoutId);
  closeTimeoutId = null;
};

const removePointerListeners = () => {
  if (typeof window === "undefined") return;

  window.removeEventListener("pointermove", handlePointerMove);
  window.removeEventListener("pointerup", handlePointerEnd);
  window.removeEventListener("pointercancel", handlePointerCancel);
};

const resetDragState = () => {
  internalDragging.value = false;
  internalTranslateY.value = 0;
  internalDragStartY.value = 0;
  internalDragDidMove.value = false;
  suppressHandleClick.value = false;
  activePointerId.value = null;
  dragStartedFromHandle.value = false;
  touchStartY.value = null;
};

const shouldUseTouchGuard = () =>
  isModernMode.value &&
  isSheetRendered.value &&
  (isSheetOpen.value || isSheetDragging.value);

const finalizeClose = () => {
  cancelPendingOpen();
  clearCloseTimeout();
  internalRendered.value = false;
  internalOpen.value = false;
  resetDragState();
  releaseScrollLock();
};

const scheduleCloseFinalize = () => {
  if (typeof window === "undefined") {
    finalizeClose();
    return;
  }

  clearCloseTimeout();
  closeTimeoutId = window.setTimeout(() => {
    if (!internalOpen.value && !internalDragging.value) {
      finalizeClose();
    }
  }, SHEET_TRANSITION_DURATION_MS + 50);
};

const openManagedSheet = () => {
  if (!isModernMode.value || !props.modelValue) return;

  cancelPendingOpen();
  clearCloseTimeout();
  internalRendered.value = true;
  internalOpen.value = false;
  resetDragState();
  ensureScrollLock();

  void nextTick(() => {
    if (!internalRendered.value) return;

    if (typeof window === "undefined") {
      internalOpen.value = true;
      return;
    }

    openFrameId = window.requestAnimationFrame(() => {
      // Paint the closed position before entering, including on first mount.
      openFrameId = window.requestAnimationFrame(() => {
        openFrameId = null;
        if (!internalRendered.value || !props.modelValue) return;
        internalOpen.value = true;
      });
    });
  });
};

const closeManagedSheet = ({
  immediate = false,
  emitUpdate = false,
}: {
  immediate?: boolean;
  emitUpdate?: boolean;
} = {}) => {
  if (emitUpdate && isModernMode.value) {
    emit("update:modelValue", false);
    emit("close");
  }

  if (!isModernMode.value) return;

  cancelPendingOpen();
  removePointerListeners();

  if (immediate || !internalRendered.value) {
    finalizeClose();
    return;
  }

  internalOpen.value = false;
  internalDragging.value = false;
  internalTranslateY.value = 0;
  internalDragStartY.value = 0;
  internalDragDidMove.value = false;
  activePointerId.value = null;
  dragStartedFromHandle.value = false;
  scheduleCloseFinalize();
};

const finishDrag = (shouldClose: boolean) => {
  removePointerListeners();
  suppressHandleClick.value = internalDragDidMove.value;

  if (shouldClose) {
    closeManagedSheet({ emitUpdate: true });
    return;
  }

  internalDragging.value = false;
  internalTranslateY.value = 0;
  internalDragStartY.value = 0;
  internalDragDidMove.value = false;
  activePointerId.value = null;
  dragStartedFromHandle.value = false;
};

function handlePointerMove(event: PointerEvent) {
  if (activePointerId.value !== event.pointerId) return;

  const dragDelta = event.clientY - internalDragStartY.value;
  const nextTranslateY = Math.max(dragDelta, 0);

  if (!internalDragging.value) {
    const contentAtTop = (sheetContentElement.value?.scrollTop ?? 0) <= 0;
    const canStartDrag =
      dragDelta > 0 && (dragStartedFromHandle.value || contentAtTop);

    if (!canStartDrag || nextTranslateY <= 0) {
      return;
    }

    internalDragging.value = true;
  }

  internalTranslateY.value = nextTranslateY;

  if (nextTranslateY > 6) {
    internalDragDidMove.value = true;
  }

  if (event.cancelable) {
    event.preventDefault();
  }
}

function handlePointerEnd(event: PointerEvent) {
  if (activePointerId.value !== event.pointerId) return;

  if (!internalDragging.value) {
    removePointerListeners();
    activePointerId.value = null;
    dragStartedFromHandle.value = false;
    return;
  }

  finishDrag(internalTranslateY.value >= SHEET_CLOSE_DRAG_THRESHOLD);
}

function handlePointerCancel(event: PointerEvent) {
  if (activePointerId.value !== event.pointerId) return;

  if (!internalDragging.value) {
    removePointerListeners();
    activePointerId.value = null;
    dragStartedFromHandle.value = false;
    return;
  }

  finishDrag(false);
}

const startDrag = (event: PointerEvent, startedFromHandle = false) => {
  if (!isModernMode.value || !props.draggable) return;
  if (event.pointerType === "mouse" && event.button !== 0) return;

  removePointerListeners();
  activePointerId.value = event.pointerId;
  dragStartedFromHandle.value = startedFromHandle;
  internalDragging.value = false;
  internalDragStartY.value = event.clientY;
  internalTranslateY.value = 0;
  internalDragDidMove.value = false;
  suppressHandleClick.value = false;

  window.addEventListener("pointermove", handlePointerMove);
  window.addEventListener("pointerup", handlePointerEnd);
  window.addEventListener("pointercancel", handlePointerCancel);
};

const handleBackdropClick = () => {
  emit("backdrop-click");

  if (!isModernMode.value || !props.closeOnBackdrop) return;
  closeManagedSheet({ emitUpdate: true });
};

const handleHandleClick = () => {
  emit("handle-click");

  if (!isModernMode.value || !props.closeOnHandle) return;

  if (suppressHandleClick.value) {
    suppressHandleClick.value = false;
    return;
  }

  closeManagedSheet({ emitUpdate: true });
};

const handleSheetPointerDown = (event: PointerEvent) => {
  emit("sheet-pointerdown", event);

  if (!isModernMode.value) return;
  startDrag(event, false);
};

const handleSheetTouchStart = (event: TouchEvent) => {
  if (!shouldUseTouchGuard()) return;
  if (event.touches.length !== 1) return;

  touchStartY.value = event.touches[0].clientY;
};

const handleSheetTouchMove = (event: TouchEvent) => {
  if (!shouldUseTouchGuard()) return;
  if (touchStartY.value === null || event.touches.length !== 1) return;

  const deltaY = event.touches[0].clientY - touchStartY.value;
  if (deltaY <= SHEET_TOUCH_GUARD_DELTA_THRESHOLD) return;

  const contentAtTop = (sheetContentElement.value?.scrollTop ?? 0) <= 0;
  if (!contentAtTop) return;

  if (event.cancelable) {
    event.preventDefault();
  }
};

const handleSheetTouchEnd = () => {
  touchStartY.value = null;
};

const handleSheetTouchCancel = () => {
  touchStartY.value = null;
};

const handleHandlePointerDown = (event: PointerEvent) => {
  emit("handle-pointerdown", event);

  if (!isModernMode.value) return;
  startDrag(event, true);
};

const handleSheetTransitionEnd = (event: TransitionEvent) => {
  emit("sheet-transitionend", event);

  if (!isModernMode.value) return;
  if (event.target !== sheetElement.value) return;

  if (
    !["transform", "opacity"].includes(event.propertyName) ||
    internalOpen.value ||
    internalDragging.value
  ) {
    return;
  }

  finalizeClose();
};

const handleDocumentKeydown = (event: KeyboardEvent) => {
  if (event.key !== "Escape") return;
  if (!isModernMode.value || !props.closeOnEsc) return;
  if (!internalOpen.value && !internalRendered.value) return;

  closeManagedSheet({ emitUpdate: true });
};

watch(
  () => props.modelValue,
  (nextOpen) => {
    if (!isModernMode.value) return;

    if (nextOpen) {
      openManagedSheet();
      return;
    }

    closeManagedSheet();
  },
  { immediate: true }
);

watch(isModernMode, (nextIsModern) => {
  if (nextIsModern) return;

  cancelPendingOpen();
  clearCloseTimeout();
  removePointerListeners();
  releaseScrollLock();
  resetDragState();
});

onMounted(() => {
  isClientMounted.value = true;

  if (typeof window !== "undefined") {
    window.addEventListener("keydown", handleDocumentKeydown);
  }
});

onBeforeUnmount(() => {
  if (typeof window !== "undefined") {
    window.removeEventListener("keydown", handleDocumentKeydown);
  }

  cancelPendingOpen();
  clearCloseTimeout();
  removePointerListeners();
  releaseScrollLock();
});
</script>
